import React, {FC, useCallback, useEffect, useState} from 'react'
import {useHistory} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {PageTitle} from '../../../../_metronic/layout/core'
import {ConfirmModal} from '../../../../shared/components/ConfirmModal'
import {showErrorToast, showSuccessToast} from '../../../../shared/components/ToastUtils'
import {apiErrorMessage} from '../../public/PublicModels'
import {emptyFilters, IListFilters, SearchFilterBar} from '../shared/SearchFilterBar'
import {useJobProcessOptions} from '../shared/useJobProcessOptions'
import {HR_TRACKING_PATH} from '../tracking/TrackingModel'
import {deleteApplication, searchApplications, startApplicationProcess, updateApplication} from './Api'
import {fullName, IApplicationModel, IApplicationUpdateRequest} from './ApplicationModel'
import {ApplicationTable} from './ApplicationTable'
import {ApplicationDetailModal} from './ApplicationDetailModal'
import {ApplicationEditModal} from './ApplicationEditModal'
import {AssignCallPoolModal, IAssignTarget} from '../call-pool/AssignCallPoolModal'
import {SelectionToolbar, useSelection} from '../shared/Selection'
import {ITagTarget, removeCandidateTag, TagAssignModal} from '../tag/TagAssignModal'
import {useTagOptions} from '../tag/useTagOptions'
import {ITagModel} from '../tag/TagModel'

/** Gelen başvurular: arama, ilan/süreç filtresi, detay, düzenleme, silme ve süreci başlatma */
const ApplicationPage: FC = () => {
  const history = useHistory()
  const {jobs, processes} = useJobProcessOptions()
  const [filters, setFilters] = useState<IListFilters>(emptyFilters)
  const [applications, setApplications] = useState<IApplicationModel[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [editingItem, setEditingItem] = useState<IApplicationModel | null>(null)
  const [deletingItem, setDeletingItem] = useState<IApplicationModel | null>(null)
  const [tagTarget, setTagTarget] = useState<ITagTarget | null>(null)
  const [assignTarget, setAssignTarget] = useState<IAssignTarget | null>(null)
  const selection = useSelection()
  const {tags, reloadTags} = useTagOptions()

  const fetchApplications = useCallback(async (page: number, activeFilters: IListFilters) => {
    setLoading(true)
    try {
      const payload = (await searchApplications(activeFilters, page)).data.payload
      setApplications(payload.content)
      setTotalPages(payload.totalPages)
      setCurrentPage(page)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Başvurular yüklenirken hata oluştu'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchApplications(0, filters)
  }, [filters, fetchApplications])

  const handleSearch = useCallback((q: string) => setFilters((current) => ({...current, q})), [])
  const handleFilter = useCallback(
    (selection: Omit<IListFilters, 'q'>) => setFilters((current) => ({...current, ...selection})),
    []
  )

  const handlePageChange = (page: number) => {
    if (page >= 0 && page < totalPages) fetchApplications(page, filters)
  }

  const handleStartProcess = async (application: IApplicationModel) => {
    if (!application.id) return
    setLoading(true)
    try {
      await startApplicationProcess(application.id)
      showSuccessToast(`${fullName(application)} için süreç başlatıldı. Aday Takip ekranından ilerletebilirsiniz.`)
      await fetchApplications(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Süreç başlatılamadı'))
      setLoading(false)
    }
  }

  const toTagTarget = (application: IApplicationModel): ITagTarget | null =>
    application.candidateId
      ? {candidateId: application.candidateId, candidateName: fullName(application), tags: application.tags || []}
      : null

  const handleRemoveTag = async (application: IApplicationModel, tag: ITagModel) => {
    const target = toTagTarget(application)
    if (!target) return
    try {
      await removeCandidateTag(target, tag)
      await fetchApplications(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Etiket kaldırılamadı'))
    }
  }

  const handleUpdate = async (request: IApplicationUpdateRequest) => {
    if (!editingItem?.id) return
    setSaving(true)
    try {
      await updateApplication(editingItem.id, request)
      showSuccessToast('Başvuru güncellendi')
      setEditingItem(null)
      await fetchApplications(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Başvuru güncellenemedi'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!deletingItem?.id) return
    setLoading(true)
    try {
      await deleteApplication(deletingItem.id)
      showSuccessToast('Başvuru silindi')
      await fetchApplications(currentPage, filters)
    } catch (error) {
      showErrorToast(apiErrorMessage(error, 'Başvuru silinemedi'))
      setLoading(false)
    } finally {
      setDeletingItem(null)
    }
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Gelen Başvurular</PageTitle>

      <div className='card'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Gelen Başvurular</span>
            <span className='text-muted mt-1 fw-bold fs-7'>
              Süreci başlatılan adaylar Aday Takip ekranında izlenir
            </span>
          </h3>
          <div className='card-toolbar'>
            <button type='button' className='btn btn-sm btn-light-primary' onClick={() => history.push(HR_TRACKING_PATH)}>
              <i className='bi bi-kanban'></i>
              Aday Takip
            </button>
          </div>
        </div>
        <div className='card-body pt-0'>
          <SearchFilterBar
            jobs={jobs}
            processes={processes}
            tags={tags}
            onSearch={handleSearch}
            onFilter={handleFilter}
          />
          <SelectionToolbar
            pageIds={applications.map((application) => application.id || '').filter(Boolean)}
            selectedIds={selection.selectedIds}
            onTogglePage={selection.togglePage}
            onClear={selection.clear}
            onTransfer={() =>
              setAssignTarget({applicationIds: selection.selectedIds, label: `${selection.selectedIds.length} başvuru`})
            }
          />
          <ApplicationTable
            data={applications}
            selectedIds={selection.selectedIds}
            onToggleSelect={selection.toggle}
            onAssignCall={(application) =>
              application.id && setAssignTarget({applicationIds: [application.id], label: fullName(application)})
            }
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onDetail={(application) => setDetailId(application.id || null)}
            onEdit={setEditingItem}
            onStartProcess={handleStartProcess}
            onTag={(application) => setTagTarget(toTagTarget(application))}
            onRemoveTag={handleRemoveTag}
            onDelete={setDeletingItem}
            loading={loading}
          />
        </div>
      </div>

      <ApplicationDetailModal applicationId={detailId} onClose={() => setDetailId(null)} />

      <AssignCallPoolModal
        target={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={selection.clear}
      />

      <TagAssignModal
        target={tagTarget}
        onClose={() => setTagTarget(null)}
        onSaved={() => {
          reloadTags()
          fetchApplications(currentPage, filters)
        }}
      />

      <ApplicationEditModal
        application={editingItem}
        loading={saving}
        onClose={() => setEditingItem(null)}
        onSubmit={handleUpdate}
      />

      <ConfirmModal
        show={!!deletingItem}
        header='Silme Onayı'
        text={`${deletingItem ? fullName(deletingItem) : ''} adayının başvurusu silinecek${
          deletingItem?.trackingId ? ' ve aday takip kaydı da kaldırılacak' : ''
        }. Aday, havuzda kalır. Devam edilsin mi?`}
        handleClose={() => setDeletingItem(null)}
        applyOnClick={handleDelete}
      />

      <ToastContainer position='top-right' autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
    </>
  )
}

export {ApplicationPage}
